'use client';

// `/register` клиентская форма — адаптация `/srv/sintra/frontend/app/(public)/register/page.tsx`
// под cookie/BFF-модель (ARCH-D-028, контракт §7.2 таблица переноса, §7.6 состояния UI,
// auth.cs.md §11.2).
//
// Адаптации vs эталон:
//   • `useAuth().register` → `POST /api/auth/register` (own-origin, без Authorization);
//   • consent-text приходит из RSC `page.tsx` (server-import `instance/consent-text.md`)
//     как `initialConsentText`, а не через клиентский fetch в FastAPI;
//   • submit → autologin → redirect `/news` (cookie уже стоят — route handler set их);
//   • `CONFLICT` → inline email `Пользователь с таким email уже существует`;
//   • consent-required validation: форма блокирует submit (клиент-side валидация),
//     inline на consent-поле «Необходимо дать согласие на обработку персональных данных»
//     (AUTH-CONT-D-021 — backend дублирует defense-in-depth).
//   • поле `inn` сохранено (визуальное, шлётся в API, не персистится — TD-003);
//     валидация 10–12 цифр на форме.
//
// Визуальная система (gradient/panel/typography/spacing/phone-mask) — as-is из эталона.

import { useState } from 'react';
import type { CSSProperties } from 'react';
import Link from 'next/link';
import {
  Alert,
  Badge,
  TextInput,
  PasswordInput,
  Button,
  Checkbox,
  Text,
  Box,
  Stack,
  Paper,
  Container,
  Divider,
  Title,
  Anchor,
  SimpleGrid,
  useMantineTheme,
} from '@mantine/core';
import { useAuth } from '@/lib/auth/auth-store';
import { PortalApiError } from '@/lib/api/errors';
import { useStableColorScheme } from '@/lib/use-stable-color-scheme';
import { RegionSelect } from '@/components/region/RegionSelect';
import styles from '../auth-page.module.css';

export interface RegisterFormProps {
  /** Текст согласия из `instance/consent-text.md` (или fallback) — грузится в RSC `page.tsx`. */
  initialConsentText: string;
}

const CONSENT_REQUIRED_MESSAGE = 'Необходимо дать согласие на обработку персональных данных';

export default function RegisterForm({ initialConsentText }: RegisterFormProps) {
  const theme = useMantineTheme();
  const colorScheme = useStableColorScheme();
  const { register } = useAuth();
  const [showConsent, setShowConsent] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    phone: '',
    inn: '',
    region: null as string | null,
    consent: false,
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isDark = colorScheme === 'dark';
  const pageGradient = isDark ? theme.other.pageGradientDark : theme.other.pageGradientLight;
  const panelColor = isDark ? theme.other.panelColorDark : theme.other.panelColorLight;
  const panelStrongColor = isDark ? theme.other.panelStrongColorDark : theme.other.panelStrongColorLight;
  const lineColor = isDark ? theme.other.lineColorDark : theme.other.lineColorLight;
  const mutedColor = isDark ? '#b7abd7' : '#6e6790';

  const authVars = {
    '--auth-page-gradient': pageGradient,
    '--auth-panel-bg': panelColor,
    '--auth-panel-strong': panelStrongColor,
    '--auth-line-color': lineColor,
    '--auth-muted-color': mutedColor,
    '--auth-accent-start': theme.other.brandAccentStart,
    '--auth-accent-end': theme.other.brandAccentEnd,
    '--auth-brand-shadow': theme.other.brandButtonShadow,
    '--auth-control-radius': theme.other.controlBorderRadius,
  } as CSSProperties;

  const formatPhoneNumber = (value: string) => {
    // Перенос as-is из эталона — маска `+7 (XXX) XXX-XX-XX` (контракт §2.1).
    let cleanDigits = value.replace(/\D/g, '');
    if (cleanDigits.startsWith('8')) {
      cleanDigits = '7' + cleanDigits.slice(1);
    }
    if (!cleanDigits.startsWith('7')) {
      cleanDigits = '7' + cleanDigits;
    }
    if (cleanDigits.length >= 1) {
      let formatted = '+7';
      if (cleanDigits.length > 1) {
        formatted += ' (' + cleanDigits.slice(1, 4);
        if (cleanDigits.length >= 4) {
          formatted += ')';
          if (cleanDigits.length > 4) {
            formatted += ' ' + cleanDigits.slice(4, 7);
            if (cleanDigits.length > 7) {
              formatted += '-' + cleanDigits.slice(7, 9);
              if (cleanDigits.length > 9) {
                formatted += '-' + cleanDigits.slice(9, 11);
              }
            }
          }
        }
      }
      return formatted;
    }
    return '+7 (';
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, phone: formatPhoneNumber(e.target.value) });
  };
  const handlePhoneFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value === '') setFormData({ ...formData, phone: '+7 (' });
  };
  const handlePhoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value === '+7 (' || e.target.value === '+7') {
      setFormData({ ...formData, phone: '' });
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Пожалуйста, укажите фамилию имя отчество';
    } else if (formData.full_name.trim().split(' ').length < 2) {
      newErrors.full_name = 'Пожалуйста, укажите фамилию и имя (минимум 2 слова)';
    } else if (formData.full_name.length < 5) {
      newErrors.full_name = 'ФИО должно содержать минимум 5 символов';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Пожалуйста, введите электронную почту';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Пожалуйста, введите корректный адрес электронной почты';
    }
    if (!formData.password) {
      newErrors.password = 'Пожалуйста, введите пароль';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Пароль должен содержать минимум 6 символов';
    }
    if (formData.phone && formData.phone !== '+7 (' && formData.phone.length < 18) {
      newErrors.phone = 'Пожалуйста, введите полный номер телефона';
    }
    if (formData.inn) {
      // TD-003: визуальное поле; валидация 10–12 цифр; backend молча игнорирует (не персистится).
      if (formData.inn.length !== 10 && formData.inn.length !== 12) {
        newErrors.inn = 'ИНН должен содержать 10 или 12 цифр';
      } else if (!/^\d+$/.test(formData.inn)) {
        newErrors.inn = 'ИНН должен содержать только цифры';
      }
    }
    // AUTH-CONT-D-021: consent_required — клиент-side валидация блокирует submit.
    if (!formData.consent) {
      newErrors.consent = CONSENT_REQUIRED_MESSAGE;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setSubmitError(null);
    try {
      // useAuth().register сам делает setUser + router.push('/news') (autologin, §8.1; NAV-CONT-D-010).
      // `validateForm()` уже гарантировал `consent === true`; cast узко в точке вызова (z.literal(true)).
      await register({
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || '',
        inn: formData.inn || '',
        consent_given: formData.consent as true,
        // Empty/clear → не передаём region (контракт D1 / FT-AUTH-01).
        ...(formData.region ? { region: formData.region } : {}),
      });
    } catch (error) {
      if (error instanceof PortalApiError) {
        if (error.code === 'VALIDATION_ERROR' && error.fields) {
          setErrors((current) => ({ ...current, ...error.fields }));
        }
        // CONFLICT (§2.1) → inline email «Пользователь с таким email уже существует»;
        // RATE_LIMITED (§8.0) → inline Alert «Слишком много попыток. Повторите позже.».
        setSubmitError(error.message || 'Ошибка регистрации');
      } else if (error instanceof Error) {
        setSubmitError('Произошла ошибка. Попробуйте снова.');
      } else {
        setSubmitError('Произошла ошибка. Попробуйте снова.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      component="main"
      className={styles.pageWrapper}
      style={{ background: pageGradient, ...authVars }}
      suppressHydrationWarning
    >
      <Box className={styles.mainContent} px={{ base: 'md', sm: 28 }} py={{ base: 32, sm: 48 }}>
        <Container size={560} px={0}>
          <Paper
            className={styles.card}
            radius={theme.other.panelBorderRadius}
            px={{ base: 20, sm: 36 }}
            py={{ base: 24, sm: 36 }}
            withBorder
            shadow="lg"
            style={{
              borderWidth: '1px',
              background: panelColor,
              borderColor: lineColor,
              backdropFilter: 'blur(14px)',
            }}
            suppressHydrationWarning
          >
            <Stack gap="lg">
              <Badge variant="light" className={styles.eyebrow} size="lg" radius="xl">
                Новый доступ
              </Badge>

              <Title order={1} size="h2" fw={800} ta="center" className={styles.title}>
                Регистрация
              </Title>

              <Text
                size="sm"
                c={mutedColor}
                ta="center"
                className={styles.description}
                mx="auto"
              >
                Создайте аккаунт, чтобы получить доступ к клиентскому кабинету, документам и внутренним сервисам компании.
              </Text>

              <form onSubmit={handleSubmit}>
                <Stack gap="lg">
                  {submitError && (
                    <Alert color="red" radius="md" variant="light">
                      {submitError}
                    </Alert>
                  )}

                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                    <TextInput
                      label="Фамилия Имя Отчество"
                      placeholder="Иванов Иван Иванович"
                      name="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      error={errors.full_name}
                      disabled={isLoading}
                      withAsterisk={false}
                      styles={{ root: { gridColumn: '1 / -1' } }}
                    />

                    <TextInput
                      label="Электронная почта"
                      placeholder="example@company.ru"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      error={errors.email}
                      disabled={isLoading}
                      withAsterisk={false}
                    />

                    <PasswordInput
                      label="Пароль"
                      placeholder="Введите надёжный пароль"
                      name="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      error={errors.password}
                      disabled={isLoading}
                      withAsterisk={false}
                    />

                    <TextInput
                      label="Телефон"
                      placeholder="+7 (___) ___-__-__"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      onFocus={handlePhoneFocus}
                      onBlur={handlePhoneBlur}
                      error={errors.phone}
                      disabled={isLoading}
                    />

                    <TextInput
                      label="ИНН вашей организации"
                      placeholder="10 или 12 цифр"
                      name="inn"
                      value={formData.inn}
                      onChange={(e) =>
                        setFormData({ ...formData, inn: e.target.value.replace(/\D/g, '') })
                      }
                      error={errors.inn}
                      disabled={isLoading}
                      maxLength={12}
                    />
                  </SimpleGrid>

                  <RegionSelect
                    value={formData.region}
                    onChange={(value) => {
                      setFormData({ ...formData, region: value });
                      setErrors((current) => {
                        const next = { ...current };
                        delete next.region;
                        return next;
                      });
                    }}
                    error={errors.region}
                    disabled={isLoading}
                  />

                  <Box>
                    <Checkbox
                      name="consent"
                      label={
                        <Text size="14px" fw={500} style={{ lineHeight: 1.5 }}>
                          Я даю{' '}
                          <Anchor
                            component="button"
                            type="button"
                            onClick={() => setShowConsent(!showConsent)}
                            c="violet"
                            td="underline"
                          >
                            моё согласие
                          </Anchor>
                          {' '}на обработку персональных данных
                        </Text>
                      }
                      checked={formData.consent}
                      onChange={(e) =>
                        setFormData({ ...formData, consent: e.currentTarget.checked })
                      }
                      error={errors.consent}
                      disabled={isLoading}
                    />

                    {/* Ссылка на политику — ВНЕ Checkbox label (внутри label интерферировала
                        с Playwright .check() и с Mantine Checkbox клик-таргетингом).
                        Контракт §11.2: всегда видна рядом с consent-чекбоксом. */}
                    <Text size="13px" mt={6}>
                      <Anchor
                        component={Link}
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        c="violet"
                        fw={600}
                        td="underline"
                      >
                        Политика обработки персональных данных
                      </Anchor>
                    </Text>

                    {showConsent && (
                      <Paper
                        className={styles.consentPanel}
                        mt={16}
                        p={16}
                        withBorder
                        radius="md"
                        style={{
                          maxHeight: '400px',
                          overflowY: 'auto',
                          backdropFilter: 'blur(14px)',
                        }}
                      >
                        <Text size="13px" style={{ lineHeight: 1.6, whiteSpace: 'pre-line' }} c={mutedColor}>
                          {initialConsentText}
                        </Text>
                      </Paper>
                    )}
                  </Box>

                  <Button
                    type="submit"
                    fullWidth
                    loading={isLoading}
                    mt={8}
                    variant="gradient"
                    className={styles.submitButton}
                  >
                    {isLoading ? 'Создание аккаунта...' : 'Создать аккаунт'}
                  </Button>
                </Stack>
              </form>

              <Divider
                label={<Text size="13px" c={mutedColor}>Уже есть аккаунт?</Text>}
                labelPosition="center"
                my={24}
              />

              <Text ta="center" size="14px">
                <Anchor component={Link} href="/login" c="violet" fw={600} td="none">
                  Войти в систему
                </Anchor>
              </Text>
            </Stack>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
